import { AdminProjectTemplateService } from './admin-project-template.service';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  AdminProjectTemplateListPageOpenedAction,
  AdminProjectTemplateListLoaded,
  AdminProjectTemplateApiErrored,
  AdminProjectTemplateCreatedAction,
  NewAdminProjectTemplateSubmittedAction,
  DeleteAdminProjectTemplateClickedAction,
  AdminProjectTemplateDeletedAction,
  EditAdminProjectTemplatePageOpenedAction,
  AdminProjectTemplateLoaded,
  EditAdminProjectTemplateSubmittedAction,
  AdminProjectTemplateUpdated,
} from './admin-project-template.actions';
import { catchError, map, switchMap, mergeMap, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import { Injectable } from '@angular/core';
import {
  AdminProjectTemplateApiError,
  AdminProjectTemplateApiErrorTypes,
} from './admin-project-template.errors';
import { SnackBarEffectCreatorService } from 'src/app/shared/services/effect-creators/snackbar-effect-creator.service';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';

@Injectable()
export class AdminProjectTemplateEffects {
  loadTemplates$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        AdminProjectTemplateListPageOpenedAction,
        AdminProjectTemplateCreatedAction,
        AdminProjectTemplateDeletedAction,
      ),
      switchMap(() =>
        this.projectTemplateService.loadTemplates().pipe(
          map((templates) => AdminProjectTemplateListLoaded({ templates })),
          catchError((err) =>
            of(
              AdminProjectTemplateApiErrored({
                error: new AdminProjectTemplateApiError(
                  AdminProjectTemplateApiErrorTypes.AdminProjectTemplateLoadListError,
                  err.error.error,
                ),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  loadTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EditAdminProjectTemplatePageOpenedAction),
      switchMap(({ templateId }) =>
        this.projectTemplateService.loadTemplate(templateId).pipe(
          map((template) => AdminProjectTemplateLoaded({ template })),
          catchError((err) =>
            of(
              AdminProjectTemplateApiErrored({
                error: new AdminProjectTemplateApiError(
                  AdminProjectTemplateApiErrorTypes.AdminProjectTemplateLoadTemplateError,
                  err.error.error,
                ),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  addTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NewAdminProjectTemplateSubmittedAction),
      mergeMap(({ template }) =>
        this.projectTemplateService.addTemplate(template).pipe(
          map((newTemplate) => AdminProjectTemplateCreatedAction({ template: newTemplate })),
          catchError((err) =>
            of(
              AdminProjectTemplateApiErrored({
                error: new AdminProjectTemplateApiError(
                  AdminProjectTemplateApiErrorTypes.AdminProjectTemplateCreateTemplateError,
                  err.error.error,
                ),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  closeDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AdminProjectTemplateCreatedAction),
        tap(() => {
          this.dialog.closeAll();
        }),
      ),
    { dispatch: false },
  );

  deleteTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DeleteAdminProjectTemplateClickedAction),
      mergeMap(({ templateId }) =>
        this.projectTemplateService.deleteTemplate(templateId).pipe(
          map(() => AdminProjectTemplateDeletedAction()),
          catchError((err) =>
            of(
              AdminProjectTemplateApiErrored({
                error: new AdminProjectTemplateApiError(
                  AdminProjectTemplateApiErrorTypes.AdminProjectTemplateDeleteTemplateError,
                  err.error.error,
                ),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  updateTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EditAdminProjectTemplateSubmittedAction),
      mergeMap(({ templateId, template }) =>
        this.projectTemplateService.updateTemplate(templateId, template).pipe(
          map((updatedTemplate) => AdminProjectTemplateUpdated({ template: updatedTemplate })),
          catchError((err) =>
            of(
              AdminProjectTemplateApiErrored({
                error: new AdminProjectTemplateApiError(
                  AdminProjectTemplateApiErrorTypes.AdminProjectTemplateUpdateTemplateError,
                  err.error.error,
                ),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  displayAdminProjectTemplateUpdatedSnackbar$ = this.snackBarEffectCreatorService.createEffect({
    initiatingAction: AdminProjectTemplateUpdated,
    message: () => 'Project Template successfully updated',
  });

  displayAdminProjectTemplateCreatedSnackbar$ = this.snackBarEffectCreatorService.createEffect({
    initiatingAction: AdminProjectTemplateCreatedAction,
    message: ({ template }) => `Project Template ${template.name} successfully created`,
  });

  displayAdminProjectTemplateAPIErroredSnackbar$ = this.snackBarEffectCreatorService.createEffect({
    initiatingAction: AdminProjectTemplateApiErrored,
    message: ({ error }) => this.getErrorMessage(error),
  });

  private getErrorMessage = (error: AdminProjectTemplateApiError) => {
    switch (error.type) {
      case AdminProjectTemplateApiErrorTypes.AdminProjectTemplateLoadListError:
      case AdminProjectTemplateApiErrorTypes.AdminProjectTemplateLoadTemplateError:
        return `Loading error: ${error.error.message} - please try again`;
      case AdminProjectTemplateApiErrorTypes.AdminProjectTemplateUpdateTemplateError:
        return `Updating error: ${error.error.message} - please try again`;
      case AdminProjectTemplateApiErrorTypes.AdminProjectTemplateDeleteTemplateError:
        return `Deleting error: ${error.error.message} - please try again`;
      default:
        return null;
    }
  };

  constructor(
    protected actions$: Actions,
    protected projectTemplateService: AdminProjectTemplateService,
    protected snackBarEffectCreatorService: SnackBarEffectCreatorService,
    protected dialog: MatDialog,
  ) {}
}
